import _ from 'lodash';
import { createSelector } from 'reselect';

import { LoanChangesHistoryEntityType } from 'constants/loanChanges';
import { getPersonFullName } from 'utils/loanApplication';
import { checkIsFieldDisplayed } from 'utils/loanChanges';
import {
  buildFieldPendingChangeAuthors,
  buildPendingChangesAuthors,
  checkIsAiPendingChange,
  flattenPendingChanges,
} from 'utils/pendingChangesAuthors';

import { getUuid, selectUserData } from 'store/auth/selectors';

import { selectAssignedUsers } from './assignedUsers.selectors';
import { selectLoanChangesHistory } from './loanChangesHistory.selectors';
import {
  selectPendingChangesAiRunsByExecutionId,
  selectPendingChangesHighlightAuthorKeys,
  selectSelectedPendingChangesAuthorKeys,
} from './pendingChangesAuthorsSelection.selectors';

const noFieldAuthorsByEntityKey = {};
const noLoanChanges = {};

// Views outside a loan page (and their tests) can render the author dots' wrappers without a loan
// changes history in the store.
const selectLoanChangesHistorySafe = ({ loanApplication }) =>
  loanApplication?.loanChangesHistory?.changes ?? noLoanChanges;

const selectFlatLoanChangesHistory = createSelector(
  selectLoanChangesHistory,
  flattenPendingChanges,
);

// Count only the fields the Pending Changes list and save dialog show: an added entity's hidden
// technical ID fields (IncomeId, CompanyId, …) are Direct in history but never displayed, so they
// must not inflate the per-user count. Mirrors the list's own checkIsFieldDisplayed filter; custom
// fields take a separate normalization path and are left untouched.
const selectDisplayedLoanChanges = createSelector(selectFlatLoanChangesHistory, changes =>
  changes.filter(
    change =>
      change.rootEntityTypeName === LoanChangesHistoryEntityType.CUSTOM_FIELD ||
      checkIsFieldDisplayed(change),
  ),
);

const selectPendingChangesAuthorsContext = createSelector(
  selectAssignedUsers,
  selectPendingChangesAiRunsByExecutionId,
  (assignedUsers, aiRunsByExecutionId) => ({ assignedUsers, aiRunsByExecutionId }),
);

export const selectPendingChangesAuthors = createSelector(
  selectDisplayedLoanChanges,
  getUuid,
  selectUserData,
  selectPendingChangesAuthorsContext,
  (changes, currentUserId, userData, { assignedUsers, aiRunsByExecutionId }) =>
    buildPendingChangesAuthors({
      changes,
      currentUserId,
      currentUserName: getPersonFullName(userData || {}) || null,
      assignedUsers,
      aiRunsByExecutionId,
    }),
);

export const selectPendingChangesAuthorColorMap = createSelector(
  selectPendingChangesAuthors,
  authors => _.mapValues(_.keyBy(authors, 'key'), 'color'),
);

export const selectPendingChangesAiExecutionIds = createSelector(
  selectFlatLoanChangesHistory,
  changes =>
    _.uniq(_.compact(changes.filter(checkIsAiPendingChange).map(c => c.aiProcessExecutionId))),
);

export const selectSelectedOtherPendingChangesAuthors = createSelector(
  selectPendingChangesAuthors,
  selectSelectedPendingChangesAuthorKeys,
  (authors, authorKeys) =>
    authors.filter(({ key, isCurrentUser }) => !isCurrentUser && authorKeys.includes(key)),
);

/**
 * Author rows per changed field of every entity with pending changes (history key -> field key -> rows),
 * for the author dots on read-only views. Empty with pending changes by user off, as the edit drawers
 * then show no dots either; otherwise only the selected authors' changes count, like the highlight.
 */
export const selectFieldPendingChangeAuthorsByEntityKey = createSelector(
  selectLoanChangesHistorySafe,
  selectPendingChangesHighlightAuthorKeys,
  (changesByEntity, highlightAuthorKeys) =>
    _.isNil(highlightAuthorKeys)
      ? noFieldAuthorsByEntityKey
      : _.mapValues(changesByEntity, changes =>
          buildFieldPendingChangeAuthors(changes, highlightAuthorKeys),
        ),
);

const getHistoryPropertyName = ({ propertyName }) => propertyName;

/**
 * Author rows per changed property of every entity with pending changes (history key -> history propertyName ->
 * rows), for the dots resolved from the change-history references the backend lists behind a preview field
 * (modifiedFieldDetails[].changes). Keyed by the property as recorded, so owned-entity rows (parentProperty)
 * match the backend's reference. Empty with pending changes by user off; only the selected authors count.
 */
export const selectPropertyPendingChangeAuthorsByEntityKey = createSelector(
  selectLoanChangesHistorySafe,
  selectPendingChangesHighlightAuthorKeys,
  (changesByEntity, highlightAuthorKeys) =>
    _.isNil(highlightAuthorKeys)
      ? noFieldAuthorsByEntityKey
      : _.mapValues(changesByEntity, changes =>
          buildFieldPendingChangeAuthors(changes, highlightAuthorKeys, getHistoryPropertyName),
        ),
);
